/**
 * The classes and races a message can tag, and the art for each.
 *
 * Data only, and deliberately importing nothing, so the table can be tested
 * without loading the renderer that draws from it - the same reason the emoji
 * pack list sits on its own.
 *
 * Filenames are the game's own ids, left as they arrived. Renaming them to
 * these slugs would read better in the folder and cost us the next drop of art:
 * it would land numbered again and have to be renamed by hand every time.
 */
export type GameTagKind = "class" | "race";

export type GameTag = {
  kind: GameTagKind;
  /** The spelling this entry is stored and rendered under. */
  slug: string;
  /** File under `/assets/wow/<kind>/`. */
  file: string;
  /**
   * Shown in the message.
   *
   * Left in English on purpose: these are the game's own proper nouns, and the
   * server's players use them in that form whatever language the client is in.
   */
  label: string;
};

/** Canonical slug, file, label, then any other spelling that should resolve. */
type Entry = [slug: string, file: string, label: string, ...aliases: string[]];

const CLASSES: Entry[] = [
  ["warrior", "1.png", "Warrior", "warr"],
  ["paladin", "2.png", "Paladin", "pala", "pal"],
  ["hunter", "3.png", "Hunter", "hunt"],
  ["rogue", "4.png", "Rogue"],
  ["priest", "5.png", "Priest"],
  ["deathknight", "6.png", "Death Knight", "death-knight", "dk"],
  ["shaman", "7.png", "Shaman", "sham"],
  ["mage", "8.png", "Mage"],
  ["warlock", "9.png", "Warlock", "lock"],
  ["monk", "10.png", "Monk"],
  ["druid", "11.png", "Druid"],
  ["demonhunter", "DemonHunter.png", "Demon Hunter", "demon-hunter", "dh"],
  ["evoker", "Evoker.png", "Evoker"],
];

// The `_0` half of each pair. The art ships in two genders and the tag says
// nothing about which, so one is picked rather than guessed at; `_1` is sitting
// there for the day the syntax grows a way to ask.
const RACES: Entry[] = [
  ["human", "1_0.png", "Human"],
  ["orc", "2_0.png", "Orc"],
  ["dwarf", "3_0.png", "Dwarf"],
  ["nightelf", "4_0.png", "Night Elf", "night-elf", "nelf"],
  ["undead", "5_0.png", "Undead", "forsaken"],
  ["tauren", "6_0.png", "Tauren"],
  ["gnome", "7_0.png", "Gnome"],
  ["troll", "8_0.png", "Troll"],
  ["goblin", "9_0.png", "Goblin"],
  ["bloodelf", "10_0.png", "Blood Elf", "blood-elf", "belf"],
  ["draenei", "11_0.png", "Draenei"],
  ["worgen", "22_0.png", "Worgen"],
];

function index(kind: GameTagKind, entries: Entry[]) {
  const out = new Map<string, GameTag>();

  for (const [slug, file, label, ...aliases] of entries) {
    const tag: GameTag = { kind, slug, file, label };
    for (const key of [slug, ...aliases]) out.set(`${kind}:${key}`, tag);
  }

  return out;
}

const INDEX = new Map([...index("class", CLASSES), ...index("race", RACES)]);

/** Every class and race once, under its canonical spelling. */
export const GAME_TAGS: GameTag[] = [
  ...CLASSES.map(([slug, file, label]) => ({
    kind: "class" as const,
    slug,
    file,
    label,
  })),
  ...RACES.map(([slug, file, label]) => ({
    kind: "race" as const,
    slug,
    file,
    label,
  })),
];

/**
 * Resolve a written tag.
 * @param kind class or race
 * @param token What the author wrote after it
 * @returns The tag, or nothing if it is not one we know
 */
export function lookupGameTag(kind: string, token: string) {
  return INDEX.get(`${kind.toLowerCase()}:${token.toLowerCase()}`);
}

/**
 * Whether a word names a kind of tag.
 * @param word What was written after `@`
 * @returns Whether it is one of ours
 */
export function isGameTagKind(word: string): word is GameTagKind {
  const lower = word.toLowerCase();
  return lower === "class" || lower === "race";
}

/** Where the art for a tag lives. */
export function gameTagUrl(tag: GameTag) {
  return `/assets/wow/${tag.kind}/${tag.file}`;
}
